<template>

  <!-- Backdrop / Fond de la modale, affiché seulement si openModal est true -->
  <div
    class="modal"
    @click.self="closeModal"
  >
    <!-- Contenu de la modale -->
    <div class="modal-content">
      <div class="form-group">
        <label class="form-label">
          <span v-if="firstPerson">Mon prénom</span>
          <span v-else>
            Son prénom
          </span>
        </label>

        <input
          class="form-input"
          v-model="form.name">
      </div>

      <div v-if="firstPerson" class="form-group household-location">
        <label class="form-label location-label">
          Mon pays et mon code postal
          <a
            href="#"
            class="infos"
            v-b-popover.hover.top="`Ces informations sont liées au foyer, c’est pourquoi elles ne sont demandées que dans le premier profil. Le code postal aide MMM à adapter certaines propositions à votre zone géographique, notamment la saisonnalité et, à terme, certains services locaux. Il n’est pas utilisé pour vous localiser précisément.`"
            title="Pourquoi demander mon code postal ?"
          >?</a>
        </label>
        <div class="location-grid">
          <label>Pays
            <input class="form-input" v-model="household.country_code" placeholder="FR">
          </label>
          <label>Code postal
            <input class="form-input" v-model="household.zipcode" placeholder="Ex. 67550">
          </label>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">
          <span v-if="firstPerson">Mon année de naissance</span>
          <span v-else>
            Son année de naissance
          </span>
        </label>

        <input
          class="form-input"
          type="number"
          min="1900"
          max="2900"
          v-model="form.year_of_birth">
      </div>

      <div class="form-group">
        <label class="form-label">
          <span v-if="firstPerson">Mon genre</span>
          <span v-else>
            Son genre
          </span>
        </label>

        <select
          class="form-input"
          v-model="form.gender">
          <option value="male">
            Homme
          </option>
          <option value="female">
            Femme
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label">
          <span v-if="firstPerson">Je souhaite perdre du poids</span>
          <span v-else>
            Il/Elle souhaite perdre du poids
          </span>
        </label>

        <select
          class="form-input"
          v-model="form.want_to_loose_weight">
          <option :value="false">
            Non
          </option>
          <option :value="true">
            Oui
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label">
          <span v-if="firstPerson">Ma taille en cm</span>
          <span v-else>
            Sa taille en cm
          </span>
        </label>

        <input
          class="form-input"
          type="number"
          min="0"
          v-model="form.height">
      </div>

      <div class="form-group">
        <label class="form-label">
          <span v-if="firstPerson">Mon poids en kg</span>
          <span v-else>
            Son poids en kg
          </span>
        </label>

        <input
          class="form-input"
          type="number"
          min="0"
          v-model="form.weight">
      </div>

      <p class="sport-explain">
        MMM peut adapter les portions et les apports en énergie et en glucides au planning des séances sportives.
        Choisissez « Oui » ci-dessous, puis renseignez les jours, horaires, durées et intensités des séances.
      </p>

      <div class="form-group sport-adaptation-block">
        <label class="form-label">Adapter mon alimentation à ma pratique sportive ?</label>
        <div class="sport-choice">
          <label :class="{ selected: form.sport_adaptation_enabled === false }">
            <input type="radio" v-model="form.sport_adaptation_enabled" :value="false"> Non
          </label>
          <label :class="{ selected: form.sport_adaptation_enabled === true }">
            <input type="radio" v-model="form.sport_adaptation_enabled" :value="true"> Oui
          </label>
        </div>
        <p class="sport-explain">
          <template v-if="!form.sport_adaptation_enabled">
            MMM utilise votre activité physique moyenne habituelle pour estimer vos besoins énergétiques.
          </template>
          <template v-else>
            MMM calcule vos besoins jour par jour à partir de votre activité quotidienne hors entraînement et de votre planning sportif.
          </template>
        </p>
      </div>

      <div v-if="!form.sport_adaptation_enabled" class="form-group">
        <label class="form-label">
          Temps d'activité physique moyenne par jour
          <a href="#" class="infos" v-b-popover.hover.top="`Connaitre votre temps moyen d'activité physique par jour nous aide à calculer vos besoins en apport calorique`" title="Temps moyen d'activité physique par jour">?</a>
        </label>

        <!-- Compteur global 10h -->
        <div class="activity-counter" :class="counterClass">
          <div class="activity-counter__bar">
            <div class="activity-counter__fill" :style="{ width: Math.min(totalUsed / 600 * 100, 100) + '%', background: counterFillColor }"></div>
          </div>
          <div class="activity-counter__text">
            <span>Utilisé : <strong>{{ totalDisplay }}</strong></span>
            <span>Restant : <strong>{{ remainingDisplay }}</strong> / 10h max</span>
          </div>
          <div v-if="showCombinedWarning" class="activity-counter__warning">
            ⚠️ Le total dépasse 7h — vérifiez vos saisies.
          </div>
        </div>

        <div class="grid">
          <div>
            Faible intensité <span class="max-hint">(max 6h)</span>
            <a href="#" class="infos" v-b-popover.hover.top="`Exemple: Position debout, attente passive, cuisiner, travail statique...`" title="Temps moyen d'activité physique de faible intensité par jour">?</a>
          </div>

          <InteractiveProgressBar
            v-model="form.low_intensity_activity"
            :max="360"
            :clamp-to="maxLow"
            :height="25"
            :label="humanizeActivity(form.low_intensity_activity)"
            :step="5"
            color="#7ab228" />

          <div>
            Intensité modérée <span class="max-hint">(max 6h)</span>
            <a href="#" class="infos" v-b-popover.hover.top="`Exemple: Industrie de production, menuiserie, yoga, marche...`" title="Temps moyen d'activité physique d'intensité modérée par jour">?</a>
          </div>

          <InteractiveProgressBar
            v-model="form.medium_intensity_activity"
            :max="360"
            :clamp-to="maxMedium"
            :height="25"
            :label="humanizeActivity(form.medium_intensity_activity)"
            :step="5"
            color="#e7b642" />

          <div>
            Forte intensité <span class="max-hint">(max 6h)</span>
            <a href="#" class="infos" v-b-popover.hover.top="`Exemple: Travaux du bâtiment, bêchage, ménage intensif, sport en compétition, footing...`" title="Temps d'activité physique moyenne de forte intensité par jour">?</a>
          </div>

          <InteractiveProgressBar
            v-model="form.high_intensity_activity"
            :max="360"
            :clamp-to="maxHigh"
            :height="25"
            :label="humanizeActivity(form.high_intensity_activity)"
            :step="5"
            color="#ee5e5b" />
        </div>
      </div>

      <div v-else class="form-group sport-detail-block">
        <label class="form-label activity-level-label">
          Activité quotidienne hors entraînement
          <a
            href="#"
            class="infos"
            v-b-popover.hover.top="`Faible : journée majoritairement assise. Modérée : déplacements réguliers, marche, journée assez active. Élevée : métier physique, beaucoup de marche ou activité quotidienne importante. Les séances du planning sont ajoutées séparément et ne sont pas comptées deux fois.`"
            title="Activité quotidienne hors entraînement"
          >?</a>
        </label>
        <select class="form-input" v-model="form.daily_activity_level">
          <option value="low">Faible</option>
          <option value="moderate">Modérée</option>
          <option value="high">Élevée</option>
        </select>

        <div class="planning-heading">
          <div>
            <strong>Mon planning d’entraînement et de compétition</strong>
            <p>MMM utilise ces séances pour adapter l’énergie et les glucides de chaque journée.</p>
          </div>
          <button type="button" class="button small-button" @click="addTraining">+ Ajouter une séance</button>
        </div>

        <div v-if="!trainings.length" class="planning-empty">
          Ajoutez vos entraînements habituels ou vos compétitions.
        </div>

        <div v-for="(training, index) in trainings" :key="'training-' + index" class="training-card">
          <div class="training-grid">
            <label>Jour
              <select class="form-input" v-model.number="training.day_of_week">
                <option v-for="day in weekDays" :key="day.value" :value="day.value">{{ day.label }}</option>
              </select>
            </label>
            <label class="training-label-with-help">Heure
              <span
                class="inline-help"
                tabindex="0"
                data-help="L’heure permet à MMM d’identifier automatiquement la prise alimentaire située avant et après la séance afin d’adapter les quantités, notamment glucidiques."
              >?</span>
              <input class="form-input" type="time" v-model="training.start_time">
            </label>
            <label>Type
              <select class="form-input" v-model="training.sport_type">
                <option value="collective">Sport collectif</option>
                <option value="endurance">Endurance / effort long (ex. cyclisme)</option>
                <option value="cardio">Cardio / explosivité (ex. HIIT)</option>
                <option value="strength">Renforcement / musculation</option>
                <option value="gentle">Activité douce (ex. yoga, Pilates)</option>
              </select>
            </label>
            <label>Durée (min)
              <input class="form-input" type="number" min="15" max="480" step="15" v-model.number="training.duration_minutes">
            </label>
            <label>Intensité
              <select class="form-input" v-model="training.intensity">
                <option value="moderate">Modérée</option>
                <option value="intermediate">Intermédiaire</option>
                <option value="high">Élevée</option>
              </select>
            </label>
            <label class="competition-check">
              <input type="checkbox" v-model="training.is_competition"> Compétition
            </label>
          </div>
          <button type="button" class="remove-training" @click="removeTraining(index)">Supprimer cette séance</button>
        </div>

        <div class="planning-note">
          Les séances renseignées ici remplacent l’activité sportive moyenne du profil : elles ne sont pas additionnées.
        </div>
      </div>
      
      <div style="display: flex; justify-content: flex-end; margin-top: 1rem; gap: 10px;">
        <button class="button" @click="closeModal">
          Fermer
        </button>

        <button class="button" :disabled="saving" @click="save">
          {{ saving ? "Enregistrement..." : "Sauvegarder" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import InteractiveProgressBar from '@/components/InteractiveProgressBar.vue';
import { api } from "@/api";
import { userState } from "@/utils/store";


export default {
  name: "EditActivityModal",
  components: {
    InteractiveProgressBar,
  },
  props: {
    profil: {
      type: Object,
      required: true,
    },
    firstPerson: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        name: this.profil.name,
        year_of_birth: this.profil.year_of_birth,
        low_intensity_activity: this.profil.low_intensity_activity,
        medium_intensity_activity: this.profil.medium_intensity_activity,
        high_intensity_activity:  this.profil.high_intensity_activity,
        sport_adaptation_enabled: !!this.profil.sport_adaptation_enabled,
        daily_activity_level: this.profil.daily_activity_level || "low",
        gender: this.profil.gender,
        want_to_loose_weight: this.profil.want_to_loose_weight,
        height: this.profil.height,
        weight: this.profil.weight,
      },
      saving: false,
      household: {
        country_code: userState.me?.consumer_account?.country_code || "fr",
        zipcode: userState.me?.consumer_account?.zipcode || "",
      },
      trainings: [],
      weekDays: [
        { value: 0, label: "Lundi" },
        { value: 1, label: "Mardi" },
        { value: 2, label: "Mercredi" },
        { value: 3, label: "Jeudi" },
        { value: 4, label: "Vendredi" },
        { value: 5, label: "Samedi" },
        { value: 6, label: "Dimanche" },
      ],
    };
  },
  computed: {
    totalUsed() {
      return (this.form.low_intensity_activity    || 0)
           + (this.form.medium_intensity_activity || 0)
           + (this.form.high_intensity_activity   || 0);
    },
    remaining() {
      return Math.max(0, 600 - this.totalUsed);
    },
    maxLow() {
      const free = 600 - (this.form.medium_intensity_activity || 0) - (this.form.high_intensity_activity || 0);
      return Math.max(0, Math.min(360, free));
    },
    maxMedium() {
      const free = 600 - (this.form.low_intensity_activity || 0) - (this.form.high_intensity_activity || 0);
      return Math.max(0, Math.min(360, free));
    },
    maxHigh() {
      const free = 600 - (this.form.low_intensity_activity || 0) - (this.form.medium_intensity_activity || 0);
      return Math.max(0, Math.min(360, free));
    },
    showCombinedWarning() {
      return this.totalUsed > 420;
    },
    counterClass() {
      if (this.totalUsed >= 600) return 'activity-counter--full';
      if (this.showCombinedWarning) return 'activity-counter--warn';
      return '';
    },
    counterFillColor() {
      if (this.totalUsed >= 600) return '#ee5e5b';
      if (this.showCombinedWarning) return '#e7b642';
      return '#7ab228';
    },
    totalDisplay() {
      const h = Math.floor(this.totalUsed / 60);
      const m = this.totalUsed % 60;
      return m === 0 ? `${h}h` : `${h}h ${m}min`;
    },
    remainingDisplay() {
      const h = Math.floor(this.remaining / 60);
      const m = this.remaining % 60;
      return m === 0 ? `${h}h` : `${h}h ${m}min`;
    },
  },
  watch: {
    "form.sport_adaptation_enabled"(enabled) {
      if (enabled && this.profil.id && !this.trainings.length) {
        this.loadTrainings();
      }
    },
  },
  mounted() {
    if (this.profil.id && this.form.sport_adaptation_enabled) {
      this.loadTrainings();
    }
  },
  methods: {
    async loadTrainings() {
      try {
        const response = await api.get(`/sport-planning/${this.profil.id}/`);
        this.trainings = (response.data.sessions || []).map(session => ({
          ...session,
          start_time: String(session.start_time || "").slice(0, 5),
        }));
      } catch (error) {
        console.warn("Impossible de charger le planning sportif", error);
      }
    },
    addTraining() {
      this.trainings.push({
        day_of_week: 0,
        start_time: "18:00",
        duration_minutes: 60,
        intensity: "moderate",
        sport_type: "collective",
        label: "",
        is_competition: false,
      });
    },
    removeTraining(index) {
      this.trainings.splice(index, 1);
    },
    async saveTrainings(consumerId) {
      if (!this.form.sport_adaptation_enabled) return;
      await api.put(`/sport-planning/${consumerId}/`, {
        sessions: this.trainings.map(session => ({
          day_of_week: Number(session.day_of_week),
          start_time: session.start_time,
          duration_minutes: Number(session.duration_minutes),
          intensity: session.intensity,
          sport_type: session.sport_type,
          label: session.label || "",
          is_competition: !!session.is_competition,
        })),
      });
    },
    closeModal() {
      this.$emit('close');
    },
    humanizeActivity(time) {
      const hours = Math.floor(time / 60);
      const minutes = time % 60;
      return `${hours} heures ${minutes} minutes`;
    },
    async save() {
      this.saving = true;
      try {
        let response;
        if (this.profil.id) {
          response = await api.patch(`/my-consumers/${this.profil.id}/`, this.form);
        } else {
          response = await api.post('/my-consumers/', this.form);
        }

        await this.saveTrainings(response.data.id);
        if (this.firstPerson) {
          await userState.savePreferences({
            country_code: (this.household.country_code || "fr").trim(),
            zipcode: (this.household.zipcode || "").trim(),
          }, { regenerate: false });
        }
        api.post("/regenerate-my-menus/").catch(() => {});
        this.$emit('update', response.data);
        window.location.reload();
        this.closeModal();
      } catch (error) {
        console.error("Erreur lors de l’enregistrement du profil", error);
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>

<style scoped>

.household-location { padding:14px; border:1px solid #dfe6d8; border-radius:12px; background:#f8faf5; }
.location-label { display:flex; align-items:center; gap:7px; }
.location-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.training-label-with-help { position:relative; }
.inline-help { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border:1px solid #6f9634; border-radius:50%; color:#466b22; font-size:11px; font-weight:800; cursor:help; position:relative; }
.inline-help:hover::after,.inline-help:focus::after { content:attr(data-help); position:absolute; left:50%; bottom:calc(100% + 8px); transform:translateX(-50%); width:min(320px,75vw); padding:9px 11px; border-radius:8px; background:#26351b; color:#fff; font-size:12px; line-height:1.35; z-index:90; }
.sport-choice { display:flex; gap:10px; margin:8px 0; }
.sport-choice label { border:1px solid #d8dfd0; border-radius:999px; padding:9px 18px; cursor:pointer; font-weight:700; }
.sport-choice label.selected { background:#edf6df; border-color:#7ab228; color:#466b22; }
.sport-explain, .planning-heading p, .planning-note { color:#5f6658; font-size:.9rem; margin:.35rem 0; }
.activity-level-label { display:flex; align-items:center; gap:6px; }
.planning-heading { display:flex; justify-content:space-between; gap:12px; align-items:center; margin:18px 0 10px; }
.small-button { padding:8px 12px; white-space:nowrap; }
.planning-empty { padding:12px; background:#f6f8f3; border-radius:10px; color:#66705f; }
.training-card { border:1px solid #dde4d6; border-radius:12px; padding:12px; margin:10px 0; background:#fbfcf9; }
.training-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
.training-grid label { font-weight:600; font-size:.85rem; }
.competition-check { display:flex; align-items:center; gap:7px; padding-top:28px; }
.remove-training { border:0; background:transparent; color:#9b3d33; cursor:pointer; padding:8px 0 0; font-weight:700; }
@media (max-width: 700px) {
  .training-grid { grid-template-columns:1fr; }
  .location-grid { grid-template-columns:1fr; }
  .planning-heading { align-items:flex-start; flex-direction:column; }
  .competition-check { padding-top:0; }
}

.max-hint {
  font-size: 0.75rem;
  color: #888;
}

.activity-counter {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  margin-bottom: 0.75rem;
  background: #f9f9f9;
}
.activity-counter--warn { border-color: #e7b642; background: #fffbf0; }
.activity-counter--full { border-color: #ee5e5b; background: #fff5f5; }

.activity-counter__bar {
  height: 8px;
  background: #ddd;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.4rem;
}
.activity-counter__fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.15s, background 0.15s;
}
.activity-counter__text {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  color: #555;
}
.activity-counter__warning {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  color: #b07d00;
  font-weight: 500;
}

.my-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.my-modal-container {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  min-width: 300px;
  max-width: 500px;
}


.share-container {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}


.share-btn {
  margin-right: 1rem; /* simple marge */
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  color: #fff;
}
.facebook {
  background: #3b5998;
}
.twitter {
  background: #1da1f2;
}
.linkedin {
  background: #0077B5;
}


.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: auto;
  grid-auto-flow: row;
}


</style>
